import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged, finalize } from 'rxjs/operators';
import _ from 'lodash';
import { EnumValues, ModalService } from '@card-software/library';
import { AuthService, GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ProductionCardStatus, ProductionCardStatusOption } from '@app/models/production/production-card';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { filterAssignableStatuses } from '@app/utilities/production-card-status-role.util';
import { Asset } from '../../../models/asset/asset';
import { WorkOrder } from '../../../models/order/work-order';
import { DyeingColor } from '../../../models/production/dyeing/dyeing-color';
import { ProductionCardFormGroup } from '../../../models/production/production-card-forms';
import { Tags } from '../../../models/tags/tags';
import { TagsProxyService } from '../../../proxies/item/tags.proxy.service';
import { ProductionCardsProxyService } from '../../../proxies/production/production-card.proxy.service';
import { ProductionCardService } from '../../../services/production-card.service';
import { AssignRollsToPcComponent, NeededStructureRollsPC } from '../_modals/assign-rolls-to-pc/assign-rolls-to-pc.component';

@Component({
  selector: 'app-production-card',
  templateUrl: './production-card.component.html',
  styleUrl: './production-card.component.scss',
})
export class ProductionCardComponent implements OnInit, OnChanges {
  @Input() productionCardFormGroup!: FormGroup<ProductionCardFormGroup>;
  @Input() assets: Asset[] = [];
  @Input() colors: DyeingColor[] = [];
  @Input() workOrder!: WorkOrder;
  @Input() readonly = false;
  @Input() selectedCard = false;
  @Input() isForSelection = false;
  @Input() showAssignRollButton = true;
  @Input() tags: Tags[] = [];
  @Input() filterStatusOptions = false;
  @Output() saved = new EventEmitter<void>();
  @Output() navigateToProductionCardDetails = new EventEmitter<string>();

  selectedTags: Tags[] = [];
  showMultiselect = false;
  productionCardStatus: EnumValues;
  /** Statuses the programming list offers; later statuses are moved from the Production Card details page. */
  private static readonly programmingStatuses = [
    ProductionCardStatus.Draft,
    ProductionCardStatus.Submitted,
    ProductionCardStatus.Approved,
    ProductionCardStatus.PlanningForBatching,
    ProductionCardStatus.Deleted,
    ProductionCardStatus.Rejected,
  ];
  statusOptions = signal<ProductionCardStatusOption[]>([]);

  public isLoading = 0;
  filteredTagText = '';
  canAddTag = false;

  @Output() productionCardSelected = new EventEmitter<FormGroup<ProductionCardFormGroup>>();
  @Output() productionCardUnselected = new EventEmitter<FormGroup<ProductionCardFormGroup>>();

  public isSelected = signal(false);

  public quantityToUseNewPC = new FormControl<number>(0, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(0)],
  });

  public status = new FormControl<number>(0, {
    nonNullable: true,
    validators: Validators.required,
  });

  public plannedTotalQty = new FormControl<number>({ value: 0, disabled: true });
  public checkShowDetails = new FormControl<boolean>(false);

  //#region formGroup dates popover
  batchingEstimatedDate = new FormControl<Date | null>(null);
  batchingRealDate = new FormControl<Date | null>(null);
  dyeingEstimatedDate = new FormControl<Date | null>(null);
  dyeingRealDate = new FormControl<Date | null>(null);
  finishingEstimatedDate = new FormControl<Date | null>(null);
  finishingRealDate = new FormControl<Date | null>(null);

  formGroupDates = new FormGroup({
    batchingEstimatedDate: this.batchingEstimatedDate,
    dyeingEstimatedDate: this.dyeingEstimatedDate,
    finishingEstimatedDate: this.finishingEstimatedDate,
  });

  private readonly _productionCardsProxyService = inject(ProductionCardsProxyService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly _productionCardService = inject(ProductionCardService);
  private readonly tagsService = inject(TagsProxyService);
  private readonly reportProxyService = inject(ReportProxyService);
  private readonly enumService = inject(EnumService);
  private readonly modalService = inject(ModalService);
  private readonly rollProxyService = inject(RollProxyService);

  constructor() {
    this.productionCardStatus = this.enumService.getEnum('ProductionCardStatus', 'productionEnums')?.values || {};
  }

  /**
   * Rebuilds the status options from the card's saved status. When editable, only the statuses the
   * user's roles may assign are selectable; the saved status is always listed (disabled if not
   * assignable), and the control is read-only when the user can assign none of them.
   */
  private refreshStatusOptions(): void {
    const allStatuses = Object.keys(this.productionCardStatus).map((key) => Number(key) as ProductionCardStatus);
    const statusControl = this.productionCardFormGroup.controls.status;
    const savedStatus = statusControl.getRawValue();
    const toOption = (status: ProductionCardStatus, disabled: boolean): ProductionCardStatusOption => ({
      label: this.productionCardStatus[status],
      value: status,
      disabled,
    });

    if (this.readonly) {
      this.statusOptions.set(allStatuses.map((status) => toOption(status, false)));
      return;
    }

    const candidates = this.filterStatusOptions ? ProductionCardComponent.programmingStatuses : allStatuses;
    const assignable = filterAssignableStatuses(candidates, AuthService.listRoles());
    this.statusOptions.set(
      allStatuses
        .filter((status) => assignable.includes(status) || status === savedStatus)
        .map((status) => toOption(status, !assignable.includes(status))),
    );

    if (_.isEmpty(assignable)) {
      statusControl.disable({ emitEvent: false });
    } else {
      statusControl.enable({ emitEvent: false });
    }
  }

  ngOnInit(): void {
    const colorCtrl = this.productionCardFormGroup.controls.color;
    const assetCtrl = this.productionCardFormGroup.controls.dyeingAsset;
    this.batchingEstimatedDate.setValue(
      this.productionCardFormGroup.controls.batchingEstimatedDate.value
        ? new Date(this.productionCardFormGroup.controls.batchingEstimatedDate.value)
        : null,
    );
    this.finishingEstimatedDate.setValue(
      this.productionCardFormGroup.controls.finishingEstimatedEndDate.value
        ? new Date(this.productionCardFormGroup.controls.finishingEstimatedEndDate.value)
        : null,
    );
    this.dyeingEstimatedDate.setValue(
      this.productionCardFormGroup.controls.dyeingEstimatedStartDate.value
        ? new Date(this.productionCardFormGroup.controls.dyeingEstimatedStartDate.value)
        : null,
    );

    if (this.productionCardFormGroup.value.isNew) {
      colorCtrl.setValue(null, { emitEvent: false });
      assetCtrl.setValue(null, { emitEvent: false });

      colorCtrl.setValidators([Validators.required]);
      assetCtrl.setValidators([Validators.required]);
    }

    this.quantityToUseNewPC.valueChanges.subscribe((value) => {
      if (!this.isForSelection && this.productionCardFormGroup.value.isNew) {
        this._productionCardService.updateProductionCardEntries(this.productionCardFormGroup, value);
      }
    });

    colorCtrl.valueChanges.pipe(distinctUntilChanged()).subscribe((color) => {
      if (!color || this.isForSelection || !this.productionCardFormGroup.value.isNew) {
        return;
      }
      const baseQty = this.productionCardFormGroup.value.isNew ? this.quantityToUseNewPC.value : this.productionCardTotalQty;
      this._productionCardService.updateProductionCardEntries(this.productionCardFormGroup, baseQty);
    });

    if (!this.productionCardFormGroup.value.isNew) {
      this.plannedTotalQty.setValue(this.productionCardTotalQty);
    }

    this.updateFormGroupDisabledState();
    this.refreshStatusOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productionCardFormGroup'] && this.productionCardFormGroup?.value?.isNew) {
      this.quantityToUseNewPC.setValue(0, { emitEvent: false });
    }
    if (changes['readOnly']) {
      this.updateFormGroupDisabledState();
    }
    const statusInputsChanged = changes['filterStatusOptions'] || changes['readOnly'] || changes['productionCardFormGroup'];
    if (statusInputsChanged && !_.isNil(this.productionCardFormGroup)) {
      this.refreshStatusOptions();
    }
  }

  canSave(): boolean {
    if (!this.productionCardFormGroup.controls.id.value) {
      return this.productionCardFormGroup.valid && this.quantityToUseNewPC.valid;
    }

    return this.productionCardFormGroup.valid && (this.productionCardFormGroup.dirty || this.formGroupDates.dirty);
  }

  get tagsControl(): FormControl {
    return this.productionCardFormGroup.get('tags') as FormControl;
  }

  get productionCardTotalQty() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.productionCardFormGroup.controls.productionCardEntries?.value.reduce((acc: number, entry: any) => {
      return acc + entry.quantity;
    }, 0);
  }

  getFabricByCode(fabricCode: string) {
    return this.workOrder.fabrics.find((fabric) => fabric.fabricCode === fabricCode)?.fabric;
  }
  onFilterTag(event: { filter?: string }): void {
    const filterText = event.filter?.trim().toLowerCase() || '';
    this.filteredTagText = filterText;
    this.canAddTag = !!filterText && !this.tags.some((tag) => tag.name.toLowerCase() === filterText);
  }

  addNewTag(): void {
    if (!this.filteredTagText) return;

    const newTag = {
      name: this.filteredTagText,
      tagType: 0,
    };

    this.tagsService.createTags(newTag).subscribe({
      next: (response) => {
        if (response?.data) {
          this.tags.push(response.data); // Lista de todos los tags
          const current = this.tagsControl.value || [];
          this.tagsControl.setValue([...current, response.data]); // Actualiza el form
          this.saveTags();
        }
        this.filteredTagText = '';
        this.canAddTag = false;
      },
    });
  }

  saveTags(): void {
    const id = this.productionCardFormGroup.value.id;
    const tags = this.tagsControl.value?.map((tag: Tags) => tag.id) || []; // o ajusta según estructura de tag

    if (!id) {
      const selectedTags = this.tagsControl.value?.map((tag: Tags) => tag) || [];
      this.productionCardFormGroup.patchValue({ tags: selectedTags });
      this.showMultiselect = false;
      this.selectedTags = this.tagsControl.value;
      return;
    }

    this._productionCardsProxyService.updateTagsProductionCardByID(id, tags).subscribe({
      next: () => {
        this.showMultiselect = false;
        this.selectedTags = this.tagsControl.value;
      },
    });
  }

  removeTag(tag: Tags): void {
    this.selectedTags = this.selectedTags.filter((t) => t.id !== tag.id);
    this.tagsControl.setValue(this.tagsControl.value.filter((t: Tags) => t.id !== tag.id));
    this.saveTags();
  }

  toggleMultiselect(): void {
    this.showMultiselect = !this.showMultiselect;
  }

  saveProductionCard() {
    this.isLoading = 1;
    const formValue = this.productionCardFormGroup.value;
    const productionCardData = {
      id: formValue.id ?? undefined,
      rollNumbers: formValue.rollNumbers ?? [],
      productionCardNumber: formValue.productionCardNumber ?? undefined,
      colorReference: formValue.color?.id ?? undefined,
      dyeingAssetId: formValue.dyeingAsset?.assetId ?? undefined,
      // Raw value: the status control is disabled for users who cannot assign any status.
      status: this.productionCardFormGroup.controls.status.getRawValue() ?? 0,
      workOrderReference: this.workOrder.id,
      productionCardEntries: (formValue.productionCardEntries ?? []).map((entry) => ({
        fabricCode: entry.fabricCode ?? '',
        quantity: entry.quantity ?? 0,
      })),
      dyeingEstimatedStartDate: this.formGroupDates.get('dyeingEstimatedDate')?.value
        ? this.formGroupDates.get('dyeingEstimatedDate')?.value
        : null,
      finishingEstimatedEndDate: this.formGroupDates.get('finishingEstimatedDate')?.value
        ? this.formGroupDates.get('finishingEstimatedDate')?.value
        : null,
      batchingEstimatedDate: this.formGroupDates.get('batchingEstimatedDate')?.value
        ? this.formGroupDates.get('batchingEstimatedDate')?.value
        : null,
      tagReferences: (formValue.tags ?? []).map((tag) => tag.id) || [],
      tags: (formValue.tags ?? []).map((tag) => ({
        id: tag.id,
        name: tag.name,
        tagType: 0,
      })),
    };

    console.log(productionCardData);

    this._productionCardsProxyService
      .createProductionCard(productionCardData)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (createdCard) => {
          this.productionCardFormGroup.patchValue({
            id: createdCard.data!.id,
            isNew: false,
            productionCardNumber: createdCard.data!.productionCardNumber,
            status: createdCard.data!.status,
          });
          this.refreshStatusOptions();

          // Keep deleted/rejected cards visible for tracking; the summary excludes their quantities.
          this.plannedTotalQty.setValue(this.productionCardTotalQty);
          this.saved.emit();
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
            `${this.localeHelperService.translateWithFallback('ProductionCardsMessages.created', 'Production Card created successfully')} (${createdCard.data?.productionCardNumber})`,
          );
        },
      });
  }

  private updateFormGroupDisabledState(): void {
    if (this.readonly) {
      this.productionCardFormGroup.disable();
    } else {
      this.productionCardFormGroup.enable();
      const plannedTotalQtyCtrl = this.productionCardFormGroup.get('plannedTotalQty');
      plannedTotalQtyCtrl?.disable();
    }
  }

  public onProductionCardSelected(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.isSelected.set(checkbox.checked);
    if (checkbox.checked) {
      this.productionCardSelected.emit(this.productionCardFormGroup);
    } else {
      this.productionCardUnselected.emit(this.productionCardFormGroup);
    }
  }

  getProductionCardReport() {
    this.reportProxyService.getProductionCardReport(this.productionCardFormGroup.value.id!).subscribe({
      next: (response) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `ProductionCard-${this.productionCardFormGroup.value.productionCardNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
        GlobalMessageUtil.success(
          this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
          this.localeHelperService.translateWithFallback('productionCard.reportDownloaded', 'The report has been downloaded successfully!'),
        );
      },
    });
  }

  private getProductionCardTotalQty(formGroup: FormGroup<ProductionCardFormGroup>): number {
    const entries = formGroup.controls.productionCardEntries?.value || [];
    return entries.reduce((acc: number, entry: Partial<{ fabricCode: string | null; quantity: number | null }>) => {
      return acc + (entry.quantity ?? 0);
    }, 0);
  }

  openModalAssignRollsToPc(workOrder: WorkOrder) {
    //get production card by
    const productionCard = this._productionCardService.savedProductionCards.find((pc) => pc.id === this.productionCardFormGroup.value.id);
    const totalWeight = this.getProductionCardTotalQty(this.productionCardFormGroup);
    if (!productionCard) {
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback(
          'productionCard.notSavedCannotAssignRolls',
          'Production Card that is not saved cannot be assigned rolls',
        ),
      );
      return;
    }
    if (_.isEmpty(productionCard.rollNumbers)) {
      const dataNeeded: NeededStructureRollsPC = {
        idWorkOrder: workOrder.id,
        idProductionCard: this.productionCardFormGroup.value.id!,
      };
      this.modalService.open(
        AssignRollsToPcComponent,
        { modalClass: 'modal_extralarge' },
        {
          structureRollsPC: dataNeeded,
          selectedRolls: [],
          productionCardWeightInLbs: totalWeight,
          productionCardNumber: productionCard.productionCardNumber,
        },
      );
    } else {
      this.isLoading++;
      this.rollProxyService
        .getRollsByNumbers(productionCard.rollNumbers)
        .pipe(finalize(() => this.isLoading--))
        .subscribe({
          next: ({ data }) => {
            const dataNeeded: NeededStructureRollsPC = {
              idWorkOrder: workOrder.id,
              idProductionCard: this.productionCardFormGroup.value.id!,
            };
            this.modalService.open(
              AssignRollsToPcComponent,
              { modalClass: 'modal_extralarge' },
              {
                structureRollsPC: dataNeeded,
                selectedRolls: data,
                productionCardWeightInLbs: totalWeight,
                productionCardNumber: productionCard.productionCardNumber,
              },
            );
          },
        });
    }
  }
}
